import {
  createFileRoute,
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  AlertCircle,
  ArrowLeft,
  Download,
  FileText,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { SearchInput } from "@/components/data/search-input";
import { RefreshButton } from "@/components/data/refresh-button";
import {
  ExecutionItemStatusBadge,
  ExecutionStatusBadge,
  ExecutionTypeBadge,
} from "@/components/data/status-badge";
import {
  executionsApi,
  type ExecutionItem,
  type ExecutionItemStatus,
} from "@/api/executions";
import { registrationsApi } from "@/api/registrations";
import { receiptsApi } from "@/api/receipts";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { authErrorMessage } from "@/auth/errors";
import {
  formatDateTime,
  formatDuration,
  formatDurationMs,
  formatNumber,
  localeFor,
} from "@/lib/format";

const STATUS_VALUES: (ExecutionItemStatus | "all")[] = [
  "all",
  "success",
  "failed",
  "skipped_duplicate",
  "in_progress",
];

const searchSchema = z.object({
  status: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/executions/$executionId/")({
  validateSearch: searchSchema,
  component: ExecutionDetailPage,
});

function ExecutionDetailPage() {
  const { executionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);

  // Volver por el historial conserva los filtros de la lista (viven en su URL). Un
  // `Link to="/executions"` navega sin search params y tiraba estado, tipo y fechas.
  // Fallback (entrada directa al detalle, sin historial): al listado sin filtros.
  const goBackToList = () =>
    canGoBack ? router.history.back() : navigate({ to: "/executions" });

  const status = (search.status ?? "all") as ExecutionItemStatus | "all";
  const statusFilter = status === "all" ? undefined : status;

  const exec = useQuery({
    queryKey: queryKeys.executions.detail(executionId),
    queryFn: () => executionsApi.get(executionId),
  });

  const itemsParams = { status: statusFilter, limit: 50 };
  const itemsQuery = useCursorList({
    queryKey: queryKeys.executions.items(executionId, itemsParams),
    fetcher: (params: typeof itemsParams & { cursor?: string }) =>
      executionsApi.listItems(executionId, params),
    params: itemsParams,
  });

  const retryFailedMutation = useMutation({
    mutationFn: () => executionsApi.retryFailed(executionId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.executions.all });
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      const skippedNote =
        data.skipped.length > 0
          ? ` · ${t("executions.detail.retrySkippedNote", { count: data.skipped.length })}`
          : "";
      toast.success(
        t("executions.detail.retryToast", { count: data.requeued }) +
          skippedNote,
      );
      setRetryDialogOpen(false);
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const retrySingleMutation = useMutation({
    mutationFn: (registrationId: string) =>
      registrationsApi.retry(registrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.executions.all });
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("registrations.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  // El backend filtra por status, la búsqueda local por contrato/otorgante.
  const q = (search.q ?? "").trim().toLowerCase();
  const filteredItems = q
    ? itemsQuery.items.filter(
        (i) =>
          // Con bajas el contrato y el otorgante pueden venir vacíos, así que la
          // búsqueda también entra por el VIN.
          (i.contractNumber ?? "").toLowerCase().includes(q) ||
          (i.grantorName ?? "").toLowerCase().includes(q) ||
          (i.assetSerialNumber ?? "").toLowerCase().includes(q),
      )
    : itemsQuery.items;

  const columns: Column<ExecutionItem>[] = [
    {
      id: "contract",
      header: t("executions.detail.columns.contract"),
      cell: (i) =>
        // Una baja se enlaza a SU detalle (no a la lista de inscripciones, donde no
        // está), y se rotula con lo que la identifique: contrato, si no VIN, si no folio.
        i.kind === "baja" ? (
          <Link
            to="/bajas/$bajaId"
            params={{ bajaId: i.registrationId }}
            onClick={(e) => e.stopPropagation()}
            className="font-medium hover:text-primary hover:underline"
          >
            {i.contractNumber || i.assetSerialNumber || i.folioRug || "—"}
          </Link>
        ) : (
          <Link
            to="/registrations"
            search={{ q: i.contractNumber }}
            onClick={(e) => e.stopPropagation()}
            className="font-medium hover:text-primary hover:underline"
          >
            {i.contractNumber}
          </Link>
        ),
    },
    {
      id: "grantor",
      header: t("executions.detail.columns.grantor"),
      cell: (i) => <span className="text-sm">{i.grantorName}</span>,
    },
    {
      id: "status",
      header: t("executions.detail.columns.status"),
      cell: (i) => <ExecutionItemStatusBadge status={i.status} />,
    },
    {
      id: "attempts",
      header: t("executions.detail.columns.attempts"),
      cell: (i) => (
        <span className="text-sm tabular-nums">{i.attemptsInExecution}</span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "duration",
      header: t("executions.detail.columns.duration"),
      cell: (i) => (
        <span
          className="text-sm tabular-nums text-muted-foreground"
          title={
            i.timings
              ? t("executions.detail.timingsTooltip", {
                  login: formatDurationMs(i.timings.loginMs),
                  fill: formatDurationMs(i.timings.fillMs),
                  submit: formatDurationMs(i.timings.submitMs),
                })
              : undefined
          }
        >
          {formatDurationMs(i.durationMs)}
        </span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "detail",
      header: t("executions.detail.columns.detail"),
      cell: (i) => <ItemDetailCell item={i} />,
    },
    {
      id: "actions",
      header: "",
      cell: (i) =>
        // Solo inscripciones: `retrySingleMutation` llama al API de registrations, y
        // con el id de una baja daría 404. El reintento de baja está en su detalle.
        i.status === "failed" && i.kind !== "baja" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={
              retrySingleMutation.isPending &&
              retrySingleMutation.variables === i.registrationId
            }
            onClick={(e) => {
              e.stopPropagation();
              retrySingleMutation.mutate(i.registrationId);
            }}
          >
            <RefreshCw className="size-3.5" />
            {t("registrations.actions.retry")}
          </Button>
        ) : null,
      headClassName: "w-32 text-right",
      className: "text-right",
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={goBackToList}
        className="inline-flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="size-4" />
        {t("executions.detail.back")}
      </button>

      {exec.isLoading ? (
        <ExecutionHeaderSkeleton />
      ) : exec.isError ? (
        <ExecutionErrorBanner error={exec.error} />
      ) : exec.data ? (
        <>
          <PageHeader
            title={t("executions.detail.titleAt", {
              date: formatDateTime(exec.data.scheduledAt),
            })}
            subtitle={
              <span className="inline-flex items-center gap-2">
                <ExecutionTypeBadge type={exec.data.type} />
                <ExecutionStatusBadge status={exec.data.status} />
                <span className="text-muted-foreground">
                  {t(`executions.trigger.${exec.data.triggeredBy}`)}
                  {exec.data.triggeredByUser
                    ? ` · ${exec.data.triggeredByUser}`
                    : ""}
                </span>
              </span>
            }
            actions={
              <div className="flex items-center gap-2">
                <RefreshButton
                  onClick={() => {
                    exec.refetch();
                    itemsQuery.refetch();
                  }}
                  spinning={exec.isFetching || itemsQuery.isFetching}
                />
                <Button
                  disabled={exec.data.failedCount === 0}
                  onClick={() => setRetryDialogOpen(true)}
                >
                  <RefreshCw className="size-4" />
                  {t("executions.detail.retryFailedButton")}
                  {exec.data.failedCount > 0 ? ` (${exec.data.failedCount})` : ""}
                </Button>
              </div>
            }
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <SummaryCard
              label={t("executions.detail.summary.total")}
              value={formatNumber(exec.data.totalRegistrations)}
            />
            <SummaryCard
              label={t("executions.detail.summary.success")}
              value={formatNumber(exec.data.successCount)}
              tone="success"
            />
            <SummaryCard
              label={t("executions.detail.summary.failed")}
              value={formatNumber(exec.data.failedCount)}
              tone="destructive"
            />
            <SummaryCard
              label={t("executions.detail.summary.skipped")}
              value={formatNumber(exec.data.skippedCount)}
              tone="warning"
            />
            <SummaryCard
              label={t("executions.detail.summary.duration")}
              value={formatDuration(exec.data.startedAt, exec.data.endedAt)}
            />
          </div>

          {exec.data.errorMessage ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3 mb-6">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{t("executions.detail.executionError")}</p>
                <p className="text-xs mt-0.5 text-destructive/80">
                  {exec.data.errorMessage}
                </p>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <SearchInput
          value={search.q ?? ""}
          onChange={(v) =>
            navigate({
              search: (prev) => ({ ...prev, q: v || undefined }),
              replace: true,
            })
          }
          placeholder={t("executions.detail.search")}
          className="sm:max-w-md flex-1"
        />
        <Select
          value={status}
          onValueChange={(v) =>
            navigate({
              search: (prev) => ({
                ...prev,
                status: v === "all" ? undefined : v,
              }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`executions.detail.filters.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable<ExecutionItem>
        data={filteredItems}
        columns={columns}
        getRowId={(i) => i.registrationId}
        renderExpanded={(i) => (
          <RegistroStepsPanel registrationId={i.registrationId} />
        )}
        isLoading={itemsQuery.isLoading}
        isFetching={itemsQuery.isFetching}
        error={itemsQuery.error}
        loadMore={
          !q && itemsQuery.hasNextPage
            ? {
                hasMore: true,
                isLoading: itemsQuery.isFetchingNextPage,
                onLoadMore: () => itemsQuery.fetchNextPage(),
              }
            : undefined
        }
        emptyState={
          exec.data?.type === "boleta" ? (
            // Las corridas de boleta (Proceso B) no tienen items por-registro; sus
            // PDFs viven en la página de Boletas. Mostramos un resumen con sentido
            // en vez de "no procesó registros".
            <EmptyState
              icon={FileText}
              title={t("executions.detail.boletaEmptyTitle")}
              description={t("executions.detail.boletaEmptyDescription", {
                ok: exec.data.successCount,
                fail: exec.data.failedCount,
              })}
              actions={
                <Button asChild variant="outline" size="sm">
                  <Link to="/receipts">
                    {t("executions.detail.goToReceipts")}
                  </Link>
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={FileText}
              title={
                q || status !== "all"
                  ? t("executions.detail.emptyFilteredTitle")
                  : t("executions.detail.emptyTitle")
              }
              description={
                q || status !== "all"
                  ? t("executions.detail.emptyFilteredDescription")
                  : t("executions.detail.emptyDescription")
              }
            />
          )
        }
      />

      <RetryFailedDialog
        open={retryDialogOpen}
        onOpenChange={setRetryDialogOpen}
        failedCount={exec.data?.failedCount ?? 0}
        isPending={retryFailedMutation.isPending}
        onConfirm={() => retryFailedMutation.mutate()}
      />
    </>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "success" | "destructive" | "warning";
}) {
  return (
    <Card className="h-fit">
      <CardHeader className="pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p
          className={
            tone === "success"
              ? "text-2xl font-semibold tabular-nums text-success"
              : tone === "destructive"
                ? "text-2xl font-semibold tabular-nums text-destructive"
                : tone === "warning"
                  ? "text-2xl font-semibold tabular-nums text-warning-foreground"
                  : "text-2xl font-semibold tabular-nums"
          }
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function ExecutionHeaderSkeleton() {
  return (
    <>
      <Skeleton className="h-8 w-72 mb-2" />
      <Skeleton className="h-4 w-48 mb-6" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="h-fit">
            <CardHeader className="pb-1">
              <Skeleton className="h-3 w-16" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-7 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function ExecutionErrorBanner({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3 mb-6">
      <AlertCircle className="size-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">{t("executions.detail.loadError")}</p>
        <p className="text-xs mt-0.5 text-destructive/80">
          {error instanceof Error ? error.message : t("common.error.retryHint")}
        </p>
      </div>
    </div>
  );
}

function ItemDetailCell({ item }: { item: ExecutionItem }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const downloadMutation = useMutation({
    mutationFn: async () => {
      // Si tenemos el boletaS3Key del item, la signed URL se obtiene desde el dominio receipts.
      // Como receipts indexa por registrationId, listamos esa registration y bajamos la primera.
      const page = await receiptsApi.list({
        registrationId: item.registrationId,
        limit: 1,
      });
      const receipt = page.items[0];
      if (!receipt) throw new Error(t("executions.detail.item.noReceipt"));
      return receiptsApi.download(receipt.id);
    },
    onSuccess: (data) => {
      const a = document.createElement("a");
      a.href = data.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = data.fileName;
      a.click();
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  if (item.status === "success") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono">
          {item.folioRug
            ? t("executions.detail.item.folio", { folio: item.folioRug })
            : t("executions.detail.item.folioMissing")}
        </span>
        {item.boletaS3Key ? (
          <button
            type="button"
            disabled={downloadMutation.isPending}
            onClick={() => downloadMutation.mutate()}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Download className="size-3" />
            PDF
          </button>
        ) : null}
      </div>
    );
  }
  if (item.status === "failed") {
    return (
      <span
        className="text-xs text-destructive line-clamp-1 max-w-xs"
        title={item.errorMessage}
      >
        {item.errorMessage ?? t("executions.detail.item.noErrorDetail")}
      </span>
    );
  }
  if (item.status === "skipped_duplicate") {
    return (
      <button
        type="button"
        onClick={() =>
          navigate({
            to: "/registrations",
            search: { q: item.contractNumber },
          })
        }
        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        {t("executions.detail.item.duplicateSeePrevious")}
      </button>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {t("executions.detail.item.inProgress")}
    </span>
  );
}

/**
 * Contenido colapsable de una fila: las ejecuciones de ese registro (por si hubo
 * reintentos), cada una con su TIMELINE de pasos del bot y su duración. Se
 * consulta al expandir (lazy). Sin recursos de AWS (no le interesan al usuario).
 */
function RegistroStepsPanel({ registrationId }: { registrationId: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: queryKeys.registrations.timeline(registrationId),
    queryFn: () => executionsApi.listItemsByRegistration(registrationId),
  });

  if (q.isLoading) {
    return (
      <div className="px-6 py-4 text-xs text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="px-6 py-4 text-xs text-muted-foreground">
        {t("executions.detail.item.noSteps")}
      </div>
    );
  }
  return (
    <div className="px-6 py-4 space-y-5">
      {items.map((it, idx) => (
        <div key={`${it.executionId}-${idx}`} className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <ExecutionItemStatusBadge status={it.status} />
            <span className="text-muted-foreground">
              {formatDateTime(it.startedAt)}
              {it.endedAt
                ? ` → ${new Date(it.endedAt).toLocaleTimeString(localeFor(), {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}`
                : ""}
            </span>
            {it.durationMs != null ? (
              <span className="text-muted-foreground">
                · {t("executions.detail.item.total")}{" "}
                {formatDurationMs(it.durationMs)}
              </span>
            ) : null}
          </div>

          {it.stages && it.stages.length > 0 ? (
            <ol className="relative border-l border-border pl-6 space-y-3 mt-1">
              {it.stages.map((s, i) => (
                <li key={`${s.stage}-${i}`} className="relative">
                  <span className="absolute -left-[25px] top-1 size-2.5 rounded-full bg-primary/70 ring-4 ring-muted/30" />
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm">{s.stage}</span>
                    <span className="text-xs tabular-nums text-muted-foreground shrink-0">
                      {formatDurationMs(s.ms)}
                    </span>
                  </div>
                  {s.startedAt ? (
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {new Date(s.startedAt).toLocaleTimeString(localeFor(), {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("executions.detail.item.noStageBreakdown")}
            </p>
          )}

          {it.status === "failed" && it.errorMessage ? (
            <p className="text-xs text-destructive">{it.errorMessage}</p>
          ) : null}
          {it.status === "success" && it.folioRug ? (
            <p className="text-xs">
              {t("registrations.detail.folioLabel")}{" "}
              <span className="font-mono">{it.folioRug}</span>
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RetryFailedDialog({
  open,
  onOpenChange,
  failedCount,
  isPending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  failedCount: number;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("executions.detail.retryDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("executions.detail.retryDialog.description", {
              count: failedCount,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            <Trans
              i18nKey="executions.detail.retryDialog.warning"
              components={{ strong: <span className="font-medium text-foreground" /> }}
            />
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              {t("common.actions.cancel")}
            </Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={isPending || failedCount === 0}>
            {isPending ? (
              <>
                <RefreshCw className="size-4 animate-spin" />
                {t("executions.detail.retryDialog.enqueueing")}
              </>
            ) : (
              t("executions.detail.retryDialog.enqueue", { count: failedCount })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
